import React, { useRef, useEffect, useState } from "react";
import moment from "moment";
import { useTimezone } from "../../Context/TimezoneContext";
import { Button, Card, Typography, makeStyles } from "@material-ui/core";
import PrintIcon from "@material-ui/icons/Print";
import { useEffectiveProfile } from "../../utils/useEffectiveProfile";
import { getCurrencySymbol, getTaxLabel } from "../../utils/currency";

const useStyles = makeStyles((theme) => ({
  card: {
    padding: theme.spacing(1.25),
    maxWidth: "800px",
    margin: "0 auto",
  },
  headerBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
    paddingBottom: theme.spacing(0.75),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  titleText: {
    fontSize: "1rem",
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  printButton: {
    textTransform: "none",
    padding: theme.spacing(0.5, 1.25),
    fontSize: "0.8125rem",
  },
  reportWrapper: {
    width: "100%",
    fontFamily: "Arial, sans-serif",
    "& @media print": {
      width: "80mm",
      margin: "0 5mm",
    },
  },
  headerText: {
    fontSize: "0.9375rem",
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: theme.spacing(0.5),
  },
  divider: {
    borderBottom: "2px dotted #000",
    margin: "8px 0",
  },
  dataText: {
    fontSize: "0.8125rem",
    lineHeight: 1.4,
    "& > div": {
      marginBottom: "2px",
    },
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    marginBottom: theme.spacing(1),
    "& th": {
      fontSize: "0.8125rem",
      padding: "4px 0",
      borderBottom: "1px solid #ddd",
    },
    "& td": {
      fontSize: "0.75rem",
      padding: "4px 0",
    },
  },
  summaryText: {
    fontSize: "0.8125rem",
    "& > div": {
      margin: "4px 0",
    },
  },
  flexBetween: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  sectionTitle: {
    fontSize: "0.875rem",
    fontWeight: "bold",
    margin: "10px 0 4px 0",
  },
  footer: {
    textAlign: "center",
    fontWeight: "bold",
    fontSize: "0.75rem",
    marginTop: theme.spacing(1),
  },
}));

const ThermalSalesReport = ({
  userProfile,
  salesData,
  selectedDate,
  reportType: reportTypeProp,
  selectedEmployeeName,
  users,
  selectedUserName,
}) => {
  const reportType = reportTypeProp || "consolidated";
  const classes = useStyles();
  const { formatInTimezone } = useTimezone();
  const profile = useEffectiveProfile();
  const currencySymbol = getCurrencySymbol(profile?.currency);
  const taxLabel = getTaxLabel(profile?.currency);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const printRef = useRef();

  useEffect(() => {
    const filtered = (salesData.invoices || []).filter(
      (invoice) =>
        moment(invoice.createdAt).format("YYYY-MM-DD") ===
        moment(selectedDate).format("YYYY-MM-DD")
    );
    setFilteredInvoices(filtered);
  }, [selectedDate, salesData.invoices]);

  //   const handleDateChange = (date) => {
  //     setSelectedDate(date);
  //   };

  const handlePrint = () => {
    const printContent = document.getElementById("thermal-sales-report");

    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    document.body.appendChild(iframe);

    const iframeDoc = iframe.contentWindow.document;
    iframeDoc.write(
      "<!DOCTYPE html><html><head><title>Sales Report</title>" +
        "<style>" +
        "@page { size: 80mm auto; margin: 0mm 5mm 0mm 5mm; }" +
        "@media print {" +
        "  body { margin: 0; padding: 0; width: 80mm; }" +
        '  .report-wrapper { width: 72mm; padding: 2mm 4mm; font-family: "Arial", sans-serif; }' +
        "  .text-center { text-align: center; }" +
        "  .text-right { text-align: right; }" +
        "  .bold { font-weight: bold; }" +
        "  .divider { border-bottom: 1px dotted #000; margin: 5px 0; }" +
        "  table { width: 100%; border-collapse: collapse; }" +
        "  th, td { padding: 2px 0; }" +
        "  .header-text { font-size: 14px; }" +
        "  .data-text { font-size: 12px; }" +
        "  .summary-text { font-size: 13px; }" +
        "}" +
        "</style></head><body>" +
        printContent.innerHTML +
        "</body></html>"
    );
    iframeDoc.close();

    iframe.contentWindow.onafterprint = () => {
      document.body.removeChild(iframe);
    };

    iframe.contentWindow.focus();
    iframe.contentWindow.print();
  };

  // Calculate category-wise totals
  // const categoryTotals = filteredInvoices.reduce((acc, invoice) => {
  //   invoice.items.forEach((item) => {
  //     const category = item?.item?.category?.categoryName || "Uncategorized";
  //     acc[category] =
  //       (acc[category] || 0) + item.quantity * item?.item?.units?.sale?.price;
  //   });
  //   return acc;
  // }, {});

  // A sales report must reflect what was actually charged, so the rate is read
  // only from the invoice line (`rate`, recorded at sale time). Never fall back
  // to the item master price — that changes later via edits/Future Price Lists
  // and would silently restate historical sales.
  const getItemRate = (item) => {
    const raw = item?.rate ?? item?.overridePrice ?? item?.originalRate ?? 0;
    const parsed = parseFloat(raw);
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const getItemQty = (item) => {
    const raw = item?.quantity ?? item?.qty ?? 0;
    const parsed = parseFloat(raw);
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  // Every invoice in range counts. Lines whose item no longer resolves to a
  // category are bucketed under "Uncategorized" rather than causing the whole
  // invoice to be dropped from the payment/discount/VAT totals.
  const computeTotals = (invoices) => {
    const catTotals = (invoices || []).reduce((acc, invoice) => {
      (invoice.items || []).forEach((item) => {
        const category = item?.item?.category?.categoryName || "Uncategorized";
        const lineAmount = getItemQty(item) * getItemRate(item);
        acc[category] = (acc[category] || 0) + lineAmount;
      });
      return acc;
    }, {});
    const payTotals = (invoices || []).reduce((acc, invoice) => {
      const mode = invoice.paymentMode || "Other";
      acc[mode] = (acc[mode] || 0) + (invoice.total || 0);
      return acc;
    }, {});
    const totalSalesVal = Object.values(catTotals).reduce((a, b) => a + b, 0);
    const totalDiscountVal = (invoices || []).reduce(
      (acc, invoice) => acc + (invoice.discount || 0),
      0
    );
    const totalVatVal = (invoices || []).reduce(
      (acc, invoice) => acc + (invoice.vat || 0),
      0
    );
    return {
      categoryTotals: catTotals,
      paymentTotals: payTotals,
      totalSales: totalSalesVal,
      totalDiscount: totalDiscountVal,
      totalVat: totalVatVal,
    };
  };

  // Two different questions:
  //  - "employee-wise" groups by the staff member on shift.
  //  - "user-wise" groups by the login that rang the sale, which is what an
  //    admin needs to compare other admins/cashiers. It also keeps working
  //    when shift tracking is off, because an invoice always records `creator`
  //    whereas employeeId is only set while shifts are enabled.
  const isBreakdown =
    reportType === "employee-wise" || reportType === "user-wise";

  const resolveUserName = (inv) => {
    const id = Array.isArray(inv.creator) ? inv.creator[0] : inv.creator;
    const match = (users || []).find((u) => u._id === id);
    return match?.name || match?.email || "Unknown user";
  };

  const groupKeyFor = (inv) =>
    reportType === "user-wise"
      ? resolveUserName(inv)
      : inv.employeeName || inv.employeeId || "Unassigned";

  const byEmployee = isBreakdown
    ? filteredInvoices.reduce((acc, inv) => {
        const key = groupKeyFor(inv);
        if (!acc[key]) acc[key] = [];
        acc[key].push(inv);
        return acc;
      }, {})
    : null;

  const totals = computeTotals(filteredInvoices);
  const {
    categoryTotals,
    paymentTotals,
    totalSales,
    totalDiscount,
    totalVat,
  } = totals;

  const employeeSummary =
    isBreakdown && byEmployee
      ? Object.entries(byEmployee)
          .map(([empName, invs]) => {
            const t = computeTotals(invs);
            return {
              employeeName: empName,
              orderCount: invs.length,
              totalCollection: invs.reduce((s, i) => s + (i.total || 0), 0),
              ...t,
            };
          })
          // Biggest takings first — that is the comparison an admin is making.
          .sort((a, b) => b.totalCollection - a.totalCollection)
      : [];

  // Column heading and title follow whichever breakdown is active.
  const breakdownLabel = reportType === "user-wise" ? "User" : "Employee";

  const reportTitle =
    reportType === "user-wise"
      ? selectedUserName
        ? `Daily Sales Report – ${selectedUserName}`
        : "Daily Sales Report (User-wise)"
      : reportType === "employee-wise"
      ? selectedEmployeeName
        ? `Daily Sales Report – ${selectedEmployeeName}`
        : "Daily Sales Report (Employee-wise)"
      : "Daily Sales Report";

  return (
    <Card className={classes.card}>
      <div className={classes.headerBar}>
        <Typography className={classes.titleText}>{reportTitle}</Typography>
        <Button
          variant="contained"
          color="primary"
          size="small"
          startIcon={<PrintIcon fontSize="small" />}
          onClick={handlePrint}
          className={classes.printButton}
        >
          Print Report
        </Button>
      </div>

      <div style={{ display: "none" }}>
        <div
          id="thermal-sales-report"
          className="report-wrapper"
          ref={printRef}
        >
          <div className="text-center bold header-text">
            {reportTitle}
          </div>
          <div className="text-center bold header-text">
            {userProfile.businessName}
          </div>

          <div className="divider" />

          <div className="data-text">
            <div>Date: {formatInTimezone(selectedDate, "MMM DD, YYYY")}</div>
            <div>Printed On: {formatInTimezone(new Date(), "MMM DD, YYYY HH:mm:ss")}</div>
            <div>Total Orders: {filteredInvoices.length}</div>
          </div>

          {isBreakdown && employeeSummary.length > 0 && (
            <>
              <div className="divider" />
              <div className="bold header-text">{breakdownLabel}-wise collection</div>
              <table>
                <thead>
                  <tr>
                    <th style={{ textAlign: "left" }} className="header-text">{breakdownLabel}</th>
                    <th style={{ textAlign: "right" }} className="header-text">Orders</th>
                    <th style={{ textAlign: "right" }} className="header-text">Collection ({currencySymbol})</th>
                  </tr>
                </thead>
                <tbody>
                  {employeeSummary.map((row) => (
                    <tr key={row.employeeName} className="data-text">
                      <td style={{ textAlign: "left" }}>{row.employeeName}</td>
                      <td style={{ textAlign: "right" }}>{row.orderCount}</td>
                      <td style={{ textAlign: "right" }}>{row.totalCollection.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          <div className="divider" />

          <table>
            <thead>
              <tr>
                <th style={{ textAlign: "left" }} className="header-text">
                  Category
                </th>
                <th style={{ textAlign: "right" }} className="header-text">
                  Amount({currencySymbol})
                </th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(categoryTotals).map(([category, amount]) => (
                <tr key={category} className="data-text">
                  <td style={{ textAlign: "left" }}>{category}</td>
                  <td style={{ textAlign: "right" }}>{amount.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="divider" />

          <div className="summary-text">
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Total Sales</span>
              <span>{currencySymbol}{totalSales.toFixed(2)}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Total Discount</span>
              <span>{currencySymbol}{totalDiscount.toFixed(2)}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Total {taxLabel}</span>
              <span>{currencySymbol}{totalVat.toFixed(2)}</span>
            </div>
          </div>

          <div className="divider" />
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span>Net Sales</span>
            <span>{currencySymbol}{(totalSales - totalDiscount + totalVat).toFixed(2)}</span>
          </div>
          <div className="bold header-text">Payment Mode Summary</div>
          {Object.entries(paymentTotals).map(([mode, amount]) => (
            <div
              key={mode}
              className="summary-text"
              style={{ display: "flex", justifyContent: "space-between" }}
            >
              <span>{mode}</span>
              <span>{currencySymbol}{amount.toFixed(2)}</span>
            </div>
          ))}

          <div className="divider" />

          <div className="text-center bold">*** End of Report ***</div>
        </div>
      </div>

      <div
        id="thermal-sales-report1"
        className={classes.reportWrapper}
        ref={printRef}
      >
        <div className={classes.headerText}>
          {reportTitle}
        </div>
        <div className={classes.headerText}>{userProfile.businessName}</div>

        <div className={classes.divider} />

        <div className={classes.dataText}>
          <div>Date: {formatInTimezone(selectedDate, "MMM DD, YYYY")}</div>
          <div>Printed On: {formatInTimezone(new Date(), "MMM DD, YYYY HH:mm:ss")}</div>
          <div>Total Orders: {filteredInvoices.length}</div>
        </div>

        {isBreakdown && employeeSummary.length > 0 && (
          <>
            <div className={classes.divider} />
            <div className={classes.sectionTitle}>{breakdownLabel}-wise collection</div>
            <table className={classes.table} style={{ marginTop: 8 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left" }}>{breakdownLabel}</th>
                  <th style={{ textAlign: "right" }}>Orders</th>
                  <th style={{ textAlign: "right" }}>Collection ({currencySymbol})</th>
                </tr>
              </thead>
              <tbody>
                {employeeSummary.map((row) => (
                  <tr key={row.employeeName}>
                    <td style={{ textAlign: "left" }}>{row.employeeName}</td>
                    <td style={{ textAlign: "right" }}>{row.orderCount}</td>
                    <td style={{ textAlign: "right" }}>{row.totalCollection.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        <div className={classes.divider} />

        <table className={classes.table}>
          <thead>
            <tr>
              <th style={{ textAlign: "left" }}>Category</th>
              <th style={{ textAlign: "right" }}>Amount({currencySymbol})</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(categoryTotals).map(([category, amount]) => (
              <tr key={category}>
                <td style={{ textAlign: "left" }}>{category}</td>
                <td style={{ textAlign: "right" }}>{amount.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className={classes.divider} />

        <div className={classes.summaryText}>
          <div className={classes.flexBetween}>
            <span>Total Sales</span>
            <span>{currencySymbol}{totalSales.toFixed(2)}</span>
          </div>
          <div className={classes.flexBetween}>
            <span>Total Discount</span>
            <span>{currencySymbol}{totalDiscount.toFixed(2)}</span>
          </div>
          <div className={classes.flexBetween}>
            <span>Total {taxLabel}</span>
            <span>{currencySymbol}{totalVat.toFixed(2)}</span>
          </div>
        </div>

        <div className={classes.divider} />

        <div className={classes.flexBetween}>
          <span className={classes.sectionTitle}>Net Sales</span>
          <span className={classes.sectionTitle}>
            {currencySymbol}{(totalSales - totalDiscount + totalVat).toFixed(2)}
          </span>
        </div>

        <div className={classes.sectionTitle}>Payment Mode Summary</div>
        {Object.entries(paymentTotals).map(([mode, amount]) => (
          <div key={mode} className={classes.flexBetween}>
            <span>{mode}</span>
            <span>{currencySymbol}{amount.toFixed(2)}</span>
          </div>
        ))}

        <div className={classes.divider} />

        <div className={classes.footer}>*** End of Report ***</div>
      </div>
    </Card>
  );
};

export default ThermalSalesReport;
